import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Play, RotateCw, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { REGIONS } from "@/lib/introContent";
import { fetchStoryCodes } from "@/pages/Story";
import { trackEvent } from "@/lib/analytics";
import { earnPoints } from "@/lib/usePoints";

const R = 120;
const REGION_COLOR = Object.fromEntries(REGIONS.flatMap((r) => r.codes.map((c) => [c, r.color])));
const polar = (deg, r) => { const a = ((deg - 90) * Math.PI) / 180; return [r * Math.cos(a), r * Math.sin(a)]; };

function Slice({ country, i, seg }) {
  const [x0, y0] = polar(i * seg, R);
  const [x1, y1] = polar((i + 1) * seg, R);
  const center = (i + 0.5) * seg;
  return (
    <g data-testid={`wheel-slice-${country.code}`}>
      <path d={`M0 0 L${x0} ${y0} A${R} ${R} 0 0 1 ${x1} ${y1} Z`} fill={REGION_COLOR[country.code] || "#8C5A2B"} stroke="#F4ECDC" strokeWidth="1.5" />
      <g transform={`rotate(${center}) translate(0 -${R - 22})`}>
        <image href={country.flag_url} width="22" height="15" x="-11" y="-7.5" preserveAspectRatio="xMidYMid slice" style={{ clipPath: "inset(0 round 2px)" }} />
      </g>
    </g>
  );
}

/** Spin-the-wheel: lands on a random country and opens its Story (or the country sheet when no Story exists yet). */
export default function CountryWheel({ countries, onSelect }) {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const { data: storyCodes = [] } = useQuery({ queryKey: ["story-codes"], queryFn: fetchStoryCodes, staleTime: 600000 });
  const [rot, setRot] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const pending = useRef(null);
  const seg = 360 / countries.length;
  const pool = useMemo(() => { const p = countries.filter((c) => storyCodes.includes(c.code)); return p.length ? p : countries; }, [countries, storyCodes]);

  const spin = () => {
    if (spinning || !countries.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    const center = (countries.indexOf(pick) + 0.5) * seg;
    const jitter = (Math.random() - 0.5) * seg * 0.6;
    const delta = ((360 - center - jitter) - (rot % 360) + 720) % 360;
    pending.current = pick;
    setResult(null); setSpinning(true); setRot(rot + 1800 + delta);
    trackEvent("wheel_spin", { country_code: pick.code });
  };
  const onEnd = () => { setSpinning(false); setResult(pending.current); earnPoints("wheel_spin", lang); };
  const hasStory = result && storyCodes.includes(result.code);

  return (
    <div className="rounded-2xl border border-stoneLine bg-card p-4 flex flex-col sm:flex-row items-center gap-4 min-w-0 relative overflow-hidden" data-testid="country-wheel">
      <span className="absolute -start-10 -bottom-10 w-40 h-40 rounded-full bg-ochre/10 pointer-events-none" />
      <div className="relative shrink-0 w-[200px] h-[200px] sm:w-[220px] sm:h-[220px]">
        <svg viewBox="-130 -130 260 260" className="w-full h-full drop-shadow-md" style={{ transform: `rotate(${rot}deg)`, transition: spinning ? "transform 4.4s cubic-bezier(.15,.75,.1,1)" : "none" }} onTransitionEnd={onEnd} data-testid="wheel-svg">
          {countries.map((c, i) => <Slice key={c.code} country={c} i={i} seg={seg} />)}
          <circle r={R} fill="none" stroke="#2B2420" strokeWidth="4" />
          <circle r="26" fill="#2B2420" stroke="#F4ECDC" strokeWidth="3" />
          <image href="/logo-mark.svg" x="-16" y="-16" width="32" height="32" />
        </svg>
        <svg viewBox="0 0 24 24" className="absolute -top-1 left-1/2 -translate-x-1/2 w-7 h-7 text-ochre drop-shadow" aria-hidden="true"><path d="M12 22 2 4h20z" fill="currentColor" stroke="#F4ECDC" strokeWidth="1.5" /></svg>
      </div>

      <div className="min-w-0 flex-1 flex flex-col gap-2 text-center sm:text-start">
        <span className="flex items-center justify-center sm:justify-start gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground"><Sparkles className="w-3 h-3 text-ochre" /> {t("wheel_title")}</span>
        {result ? (
          <div className="animate-fade-in" data-testid="wheel-result">
            <div className="flex items-center justify-center sm:justify-start gap-3">
              <img src={result.flag_url} alt="" className="w-12 h-8 object-cover rounded-md border border-stoneLine shadow-sm" />
              <span className="font-display font-bold text-xl text-ink leading-tight" data-testid="wheel-result-name">{result.short_names[lang]}</span>
            </div>
            <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-2">
              {hasStory ? (
                <button onClick={() => navigate(`/story/${result.code}`)} className="inline-flex items-center gap-2 rounded-full bg-ink text-sand pl-1.5 pr-4 py-1.5 text-sm font-semibold hover:bg-ochre transition-colors" data-testid="wheel-story-button">
                  <span className="w-6 h-6 rounded-full bg-ochre flex items-center justify-center"><Play className="w-3 h-3 fill-current" /></span> {t("story_watch")}
                </button>
              ) : (
                <button onClick={() => onSelect(result.code)} className="inline-flex items-center gap-1.5 rounded-full bg-ink text-sand px-4 py-1.5 text-sm font-semibold hover:bg-ochre transition-colors" data-testid="wheel-open-button">{t("wheel_open")} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></button>
              )}
              <button onClick={spin} className="inline-flex items-center gap-1.5 rounded-full border border-stoneLine px-3 py-1.5 text-sm hover:bg-sandDeep/50 transition-colors" data-testid="wheel-again-button"><RotateCw className="w-3.5 h-3.5" /> {t("wheel_again")}</button>
            </div>
          </div>
        ) : (
          <>
            <span className="font-display font-bold text-xl text-ink leading-tight">{spinning ? t("wheel_spinning") : t("wheel_cta")}</span>
            <span className="text-xs text-muted-foreground">{t("wheel_sub")}</span>
            <button onClick={spin} disabled={spinning} className="mt-1 self-center sm:self-start inline-flex items-center gap-2 rounded-full bg-ochre text-white px-5 py-2 text-sm font-semibold hover:bg-ink transition-colors disabled:opacity-60" data-testid="wheel-spin-button">
              <RotateCw className={`w-4 h-4 ${spinning ? "animate-spin" : ""}`} /> {t("wheel_spin")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
